import { useCallback, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar'
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import { AnimatePresence, motion } from 'framer-motion'
import { Link } from 'react-router-dom'

import LogOut from '@/assets/sign-out.svg?react'
import { useBottomInfiniteScroll } from '@/hooks/layout/useBottomInfiniteScroll'
import useScrollDirection from '@/hooks/layout/useScrollDirection'
import { useThemeSync } from '@/hooks/layout/useThemeSync'
import { useApi } from '@/hooks/useApi'
import useUser from '@/hooks/useUser'
import { groupConversationsByTime } from '@/lib/utils/conversation'
import { useAllConversationsStore, useConversations } from '@/store/useAllConversationsStore'
import { useCurrentConversationStore } from '@/store/useCurrentConversationStore'
import { useUIStore } from '@/store/useUIStore'
import { useGravatarSrc } from '@/utils/gravatar'
import { cn } from '../../lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu'
import AppDarkModeToggle from './AppDarkModeToggle'
import {
  ConversationItem,
  ConversationItemSkeleton,
  NewConversationItem,
} from './ConversationItem'
import { EdelmanBrandmark } from './CustomIcons'
import { useMeStore } from '@/store/useMeStore'
import { Button } from '../ui/button'
import { SidebarSimpleIcon } from '@phosphor-icons/react'

const ConversationGroup = ({ title, children }) => (
  <div className="relative mt-5 first:mt-0 last:mb-5">
    <div className="sticky z-20">
      <span className="flex h-9 items-center">
        <div className="mb-0 overflow-hidden text-ellipsis break-all px-2 pb-2 pt-3 text-xs font-semibold text-foreground/70 new-chat:text-white/65 dark:text-white/80">
          {title}
        </div>
      </span>
    </div>
    <ol>{children}</ol>
  </div>
)

export const SidebarContent = () => {
  const api = useApi()
  const { user } = useUser()
  const { logout } = useAuth0()
  const displayNameRaw =
    user?.given_name || user?.nickname || user?.email?.split('@')?.at(0) || 'Project Pilot'
  const displayName = displayNameRaw[0].toUpperCase() + displayNameRaw.slice(1)
  const { gravatarUrl } = useGravatarSrc({ email: user?.email, size: 32 })
  const conversations = useConversations()
  const loadMoreConversations = useAllConversationsStore(state => state.loadMoreConversations)
  const setToastMessage = useCurrentConversationStore(state => state.setToastMessage)
  const isLoading = useAllConversationsStore(state => state.isLoading)
  const total = useAllConversationsStore(state => state.pagination?.total) || 0
  const hasMore = conversations.length < total
  const grouped = groupConversationsByTime(conversations)

  const isStreaming = useCurrentConversationStore(
    s => s.streamPhase === 'loading' || s.streamPhase === 'streaming',
  )

  const onError = useCallback(
    err => {
      setToastMessage({
        title: `Can't load more conversations`,
        message: err.message,
        type: 'destructive',
      })
    },
    [setToastMessage],
  )

  const listRef = useRef(null)
  const handleLoadMore = useCallback(async () => {
    const el = listRef.current
    if (!el) return
    try {
      await loadMoreConversations(api, onError)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight - el.clientHeight
        })
      })
    } catch (err) {
      onError(err)
      console.error('Error loading more conversations', err)
    }
  }, [loadMoreConversations, api, onError])

  const sentinelRef = useBottomInfiniteScroll({
    scrollRootRef: listRef,
    loadMore: handleLoadMore,
    hasMore,
    isLoading,
  })

  const toggleSidebar = useUIStore(s => s.toggleSidebar)
  const scrolledDown = useScrollDirection({ target: 'nav' })
  const { isDark } = useThemeSync()
  const isNewChat = useCurrentConversationStore(state => state.id) === null

  const brandColors = isDark || isNewChat
    ? ['#FFFFFF', '#FFFFFF', '#FFFFFF']
    : ['#000000', '#000000', '#000000']

  const me = useMeStore(s => s.me)

  return (
    <div
      ref={listRef}
      className={cn(
        'relative size-full flex-1 items-start backdrop-blur-xl',
        'bg-sidebar text-sidebar-foreground',
        'new-chat:bg-[#0c1320]/94 new-chat:text-white',
        'border-r border-border/50 new-chat:border-white/12',
      )}
    >
      <nav className="relative flex size-full flex-1 flex-col overflow-y-auto px-2 transition-opacity duration-500">
        {/* Header */}
        <div
          className={cn(
            'sticky top-0 z-30 transition-shadow backdrop-blur-md',
            'bg-sidebar border-b border-border/50',
            'new-chat:bg-[#0c1320]/94 new-chat:border-white/12',
            scrolledDown
              ? '[box-shadow:var(--sharp-edge-top-shadow)]'
              : '[box-shadow:var(--sharp-edge-top-shadow-placeholder)]',
          )}
        >
          <div
            id="sidebar-header"
            className="h-[var(--sidebar-header-height)] flex items-center justify-between px-1 pl-safe-offset-[-2.5rem] pt-safe-offset-[-3rem]"
          >
            <span className="flex">
              <Link
                to={isStreaming ? '#' : '/chat'}
                aria-label="New Conversation"
                className={cn(
                  'flex size-10 items-center justify-center rounded-lg px-2 pr-safe-offset-2 pt-safe-offset-[-0.5rem]',
                  'transition-colors duration-200',
                  'new-chat:text-white/88 new-chat:hover:bg-white/10',
                  isStreaming && 'pointer-events-none opacity-50',
                )}
                onClick={toggleSidebar}
              >
                <EdelmanBrandmark
                  className="size-[26px] overflow-visible"
                  stroke={isDark || isNewChat ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.28)'}
                  strokeWidth={0.6}
                  colors={brandColors}
                />
              </Link>
            </span>
            <div className="flex items-center gap-1">
              <AppDarkModeToggle
                className={cn(
                  'size-10 rounded-lg px-2 hover:bg-black/5 dark:hover:bg-white/5',
                )}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                aria-label="Close sidebar"
                className={cn(
                  'size-10 hover:bg-black/5 dark:hover:bg-white/5',
                )}
              >
          <SidebarSimpleIcon />
              </Button>
            </div>
          </div>
        </div>

        {/* Conversation list */}
        <div id="sidebar" className="pb-4">
          <div id="history" className="mt-5 flex flex-col gap-2 text-sm first:mt-0">
            <NewConversationItem />
            <div className="flex-1 overflow-hidden">
              <div>
                {Object.entries(grouped).map(([group, items]) =>
                  items.length ? (
                    <ConversationGroup key={group} title={group}>
                      {items.map(conv => (
                        <ConversationItem
                          key={conv.id}
                          to={`/chat/${conv.id}`}
                          title={conv.title || 'Untitled Conversation'}
                          convId={conv.id}
                        />
                      ))}
                    </ConversationGroup>
                  ) : null,
                )}
                <div ref={sentinelRef} className="py-1">
                  {isLoading && (
                    <>
                      <ConversationItemSkeleton />
                      <ConversationItemSkeleton />
                      <ConversationItemSkeleton />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grow" />

        {/* Footer */}
        <div
          className={cn(
            'sticky bottom-0 z-30 flex items-center justify-between backdrop-blur-md',
            'bg-sidebar border-t border-border/50',
            'new-chat:bg-[#0c1320]/94 new-chat:border-white/12',
            'py-1.5 px-1',
            scrolledDown
              ? '[box-shadow:var(--sharp-edge-bottom-shadow)]'
              : '[box-shadow:var(--sharp-edge-bottom-shadow-placeholder)]',
          )}
        >
          <div className="relative w-full">
            <div className="flex items-center gap-2 pr-2">
              <div className="h-[var(--sidebar-header-height)] relative my-0 mr-2 flex min-h-10 w-full items-center gap-2 rounded-md px-2">
                <div className="flex items-center justify-center opacity-70">
                  <Avatar className="shrink-0">
                    <AvatarImage
                      src={me?.profile_image_url || user?.picture || gravatarUrl}
                      className="size-6 rounded-full"
                    />
                    <AvatarFallback>A</AvatarFallback>
                  </Avatar>
                </div>
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2.5 text-nowrap leading-5 text-sm">
                    <div className="text-foreground/80 truncate new-chat:text-white/92">
                      {displayName}
                    </div>
                  </div>
                  <div className="text-foreground/60 text-start new-chat:text-white/70">
                    <div className="mx-0.5 truncate font-semibold uppercase tracking-wider opacity-60 text-[10px]">
                      {user?.email?.split('@')?.at(1)?.split('.')?.at(0) || user?.org_name || ''}
                    </div>
                  </div>
                </div>
              </div>

              <DropdownMenu openDelay={0} className="z-30">
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex size-8 items-center rounded-lg px-2 py-1 transition-colors bg-gray-ui hover:bg-muted/60 new-chat:bg-white/5 hover:new-chat:bg-white/12"
                  >
                    <LogOut className="size-4 text-foreground/70 new-chat:text-white/80 dark:text-white/80" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="glass z-30 w-40 bg-white/80 dark:bg-gray-900/80"
                  align="start"
                  open={true}
                >
                  <DropdownMenuItem
                    onSelect={() => {
                      logout({ returnTo: 'https://ignite.microsoft.com/en-us' })
                    }}
                    className="text-foreground hover:new-chat:bg-white/10 new-chat:hover:text-white"
                  >
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>
    </div>
  )
}

export const SidebarWrapper = ({ children }) => {
  const isSidebarOpen = useUIStore(state => state.isSidebarOpen)
  return (
    <motion.div
      id="desktop-sidebar-wrapper"
      animate={isSidebarOpen ? { width: 'var(--sidebar-width)', opacity: 1 } : { width: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={cn('z-[21] shrink-0 overflow-x-hidden ...')}
    >
      <div className="h-full w-sidebar">
        <div id="sidebar-column" className="flex h-full min-h-0 flex-col">
          {children}
        </div>
      </div>
    </motion.div>
  )
}

export const Sidebar = () => (
  <SidebarWrapper>
    <SidebarContent/>
  </SidebarWrapper>
)
